import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  toast,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Space,
} from '@cashfree-intl/coherent';
import _find from 'lodash/find';
import _keyBy from 'lodash/keyBy';

// Providers
import { MerchantContext } from 'providers/MerchantProvider';
import { AccountContext } from 'providers/AccountProvider';

// Services
import {
  getCategories,
  toggleCategory,
  removeRecipient,
} from 'services/settings';

// Utils
import Analytics from 'utils/analytics';

// Hocs
import withReadPermission from 'hocs/withReadPermission';

// Components
import Loader from 'components/Loader';
import CanWrite from 'components/CanWrite';
import MetaTags from 'components/MetaTags';
import PageHeader from 'components/PageHeader';
import Icon from 'components/Icon';
import Modals from './Modals';
import EmailCategory from './EmailCategory';

// Constants
import { MODAL_TYPES } from '../constants';

// Styled
import { BackButtonWrapper } from 'styled/common';

const EmailNotifications = () => {
  const { accountList } = useContext(MerchantContext);
  const { accountInfo } = useContext(AccountContext);

  const [data, setData] = useState();
  const [modalType, setModalType] = useState();
  const [fetchCounter, setFetchCounter] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState();
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const newAccountId = _find(accountList, { accountName: accountInfo.name })
      .accountId;

    setSelectedAccountId(newAccountId);
  }, [accountInfo, accountList]);

  useEffect(() => {
    (async function fetchData() {
      if (!selectedAccountId) {
        return;
      }

      const queryObj = { selectedAccountId };

      setData();

      const response = await getCategories(queryObj);

      const data = _keyBy(response, 'notifSubType');

      setData(data);
    })();
  }, [fetchCounter, selectedAccountId]);

  const handleToggle = async (e, category) => {
    e.stopPropagation();

    const queryObj = { selectedAccountId };

    const body = {
      product: 'PAYOUT',
      notifType: category.notifType,
      notifSubType: category.notifSubType,
      enabled: !category.enabled,
    };

    const response = await toggleCategory(queryObj, body);

    if (!response.error) {
      setData(prev => ({
        ...prev,
        [category.notifSubType]: { ...category, enabled: !category.enabled },
      }));

      toast.success(response.message);
    }
  };

  const handleRemoveRecipient = async (e, category, email) => {
    e.stopPropagation();

    const queryObj = { selectedAccountId };

    const body = {
      product: 'PAYOUT',
      notifType: category.notifType,
      notifSubType: category.notifSubType,
      recipient: email,
    };

    const response = await removeRecipient(queryObj, body);

    if (!response.error) {
      setData(prev => ({
        ...prev,
        [category.notifSubType]: {
          ...category,
          recipients: category.recipients.filter(item => item !== email),
        },
      }));

      toast.success(response.message);
    }
  };

  const handleAccountSelect = data => {
    const newAccountId = _find(accountList, { accountId: data.accountId })
      .accountId;

    Analytics.track('Dropdown_AccountSelect', {
      value: newAccountId,
    });

    setSelectedAccountId(newAccountId);
  };

  if (!data) {
    return <Loader />;
  }

  const selectedAccount = _find(accountList, { accountId: selectedAccountId });

  return (
    <>
      <PageHeader embedKey="SETTINGS">
        <span>Settings - </span>Secure ID
        {accountList.length > 1 && (
          <Dropdown
            button
            icon={null}
            className="ml-2"
            style={{ minWidth: 196 }}
            trigger={
              <Space justifyContent="space-between">
                <Text>{selectedAccount.accountName}</Text>
                <Icon
                  name={open ? 'chevron-up' : 'chevron-down'}
                  className="ml-1"
                />
              </Space>
            }
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
          >
            <DropdownMenu style={{ minWidth: '100%' }}>
              {accountList.map(account => (
                <DropdownItem
                  data-testid={`${account.accountId}-dropdownitem`}
                  key={account.accountId}
                  onClick={() => handleAccountSelect(account)}
                >
                  <Space gap={0} direction="column">
                    <Text color="primary" style={{ whiteSpace: 'pre-wrap' }}>
                      {account.accountName}
                    </Text>
                    <Text variant="b12" color="bodyLight">
                      {account.rechargeAccount}
                    </Text>
                  </Space>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}
      </PageHeader>

      <MetaTags title="Settings – Secure ID" />

      <BackButtonWrapper onClick={() => navigate(-1)}>
        <Icon name="chevron-left" />
        <Text as="a" className="link ml-1">
          Back
        </Text>
        <Text as="span" variant="h16" className="ml-2">
          Email Notifications
        </Text>
      </BackButtonWrapper>

      <Text className="mt-3 mb-1">Merchant Notifications</Text>
      <CanWrite code={22007} remove>
        <Space
          justifyContent="space-between"
          alignItems="center"
          className="mb-2"
        >
          <Text as="span" color="bodyLight">
            Configure the email category that you want to get notified about.
            Click Add Recipient to add members in your organization to receive
            the emails.
          </Text>
          <Button
            primary
            onClick={() => setModalType(MODAL_TYPES.ADD_RECIPIENT)}
          >
            Add Recipient
          </Button>
        </Space>
      </CanWrite>

      {Object.keys(data).map(key => (
        <EmailCategory
          key={key}
          category={data[key]}
          onToggle={handleToggle}
          onRemoveRecipient={handleRemoveRecipient}
        />
      ))}

      {modalType && (
        <Modals
          modalType={modalType}
          setModalType={setModalType}
          setFetchCounter={setFetchCounter}
          selectedAccountId={selectedAccountId}
          data={data}
        />
      )}
    </>
  );
};

export default withReadPermission(EmailNotifications, {
  code: 22006,
  description: 'access Email Notifications',
});
